import React, { useEffect, useState, Suspense } from "react";
import { Card, Nav, Tab, Spinner, Button } from "@themesberg/react-bootstrap";
import { useParams } from "react-router-dom";
import api from "../../utils/api";
import Swal from "sweetalert2";
import { useAuth } from "../../context/AuthContext";
/* import BasicDetails from "./BasicDetails";
import GalleryTab from "./GalleryTab";
import BlockedTab from "./BlockedTab";
import MatchesTab from "./MatchesTab";
import FavoritesTab from "./FavoritesTab";
import GroupsTab from "./GroupsTab";
import SubscriptionsTab from "./SubscriptionsTab"; */


// Lazy Load Components
const LoadBasicDetails = React.lazy(() => import("./BasicDetails"));
const LoadGalleryTab = React.lazy(() => import("./GalleryTab"));
const LoadBlockedTab = React.lazy(() => import("./BlockedTab"));
const LoadMatchesTab = React.lazy(() => import("./MatchesTab"));
const LoadFavoritesTab = React.lazy(() => import("./FavoritesTab"));
const LoadGroupsTab = React.lazy(() => import("./GroupsTab"));
const LoadSubscriptionsTab = React.lazy(() => import("./SubscriptionsTab"));
const LoadDocumentsTab = React.lazy(() => import("./DocumentTab"));

export default function UserDetails() {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const { currentUser } = useAuth();

    const fetchData = async () => {
        try {
            const { data } = await api.get(`/users/${id}`);
            if (!data.error) setUser(data.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [id]);

    if (loading || !user)
        return (
            <div className="text-center mt-5">
                <Spinner animation="border" />
                <p>Loading user details...</p>
            </div>
        );

    const handleReportUser = async () => {
        const token = localStorage.getItem('token') || '';
        const isSelf = currentUser?.id && Number(currentUser.id) === Number(id);
        if (isSelf) {
            await Swal.fire({ title: 'Not allowed', text: 'You cannot report yourself.', icon: 'warning', heightAuto: false });
            return;
        }

        const result = await Swal.fire({
            title: 'Report user',
            html: `
              <div style="text-align:left;">
                <label><b>Reason</b></label>
                <input id="swal-report-reason" class="swal2-input" placeholder="e.g. Spam, Harassment..." />
                <label style="margin-top:10px;"><b>Notes (optional)</b></label>
                <textarea id="swal-report-notes" class="swal2-textarea" placeholder="Additional details..."></textarea>
              </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Submit report',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const reason = (document.getElementById('swal-report-reason')?.value || '').toString().trim();
                const notes = (document.getElementById('swal-report-notes')?.value || '').toString().trim();
                if (!reason || reason.length < 3) {
                    Swal.showValidationMessage('Reason is required (min 3 characters).');
                    return false;
                }
                return { reason, notes: notes.length ? notes : '' };
            }
        });

        if (!result.isConfirmed) return;
        try {
            const payload = result.value;
            const res = await fetch('/api/reports', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    entity_type: 'user',
                    entity_id: Number(id),
                    reason: payload.reason,
                    notes: payload.notes || null,
                }),
            });

            const json = await res.json();
            if (!res.ok || json.error) throw new Error(json.message || 'Failed to submit report');

            await Swal.fire({ title: 'Report submitted', text: 'Thanks for helping keep the community safe.', icon: 'success', heightAuto: false });
        } catch (e) {
            await Swal.fire({ title: 'Error', text: e?.message || 'Failed to submit report', icon: 'error', heightAuto: false });
        }
    };

    return (
        <Card className="p-4 shadow-lg border-0">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0 fw-bold">User Details</h3>
                <Button variant="outline-danger" size="sm" onClick={handleReportUser} disabled={loading}>
                    Report
                </Button>
            </div>

            <Tab.Container defaultActiveKey="basic">
                <Nav variant="tabs" className="mb-4">
                    <Nav.Item><Nav.Link eventKey="basic">Basic</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="documents">Identity Documents</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="gallery">Gallery</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="blocked">Blocked</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="matches">Matches</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="favorite">Favorites</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="group">Groups</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="subscription">Subscription</Nav.Link></Nav.Item>
                </Nav>

                <Tab.Content>
                    <Suspense fallback={<Spinner />}>
                        <Tab.Pane eventKey="basic">
                            <LoadBasicDetails user={user} />
                        </Tab.Pane>

                        <Tab.Pane eventKey="documents">
                            <LoadDocumentsTab documents={user.documents} />
                        </Tab.Pane>

                        <Tab.Pane eventKey="gallery">
                            <LoadGalleryTab images={user.user_images} />
                        </Tab.Pane>

                        <Tab.Pane eventKey="blocked">
                            <LoadBlockedTab
                                blocked={user.blockedData?.rows ?? []}
                                blockedCount={user.blockedData?.count ?? 0}
                            />
                        </Tab.Pane>

                        <Tab.Pane eventKey="matches">
                            <LoadMatchesTab
                                matches={user.matchesList ?? []}
                                matchesCount={user.matchesCount ?? 0}
                            />
                        </Tab.Pane>

                        <Tab.Pane eventKey="favorite">
                            <LoadFavoritesTab
                                favorites={user.favoritesData?.rows ?? []}
                                favoritesCount={user.favoritesData?.count ?? 0}
                            />
                        </Tab.Pane>

                        <Tab.Pane eventKey="group">
                            <LoadGroupsTab
                                groups={user.groupsData?.rows ?? []}
                                groupsCount={user.groupsData?.count ?? 0}
                            />
                        </Tab.Pane>

                        <Tab.Pane eventKey="subscription">
                            <LoadSubscriptionsTab
                                subscriptions={user.subscriptionsData?.rows ?? []}
                                subscriptionsCount={user.subscriptionsData?.count ?? 0}
                            />
                        </Tab.Pane>
                    </Suspense>
                </Tab.Content>
            </Tab.Container>

            <style>
                {`
                    .gallery-img-wrapper { overflow: hidden; border-radius: 10px; }
                    .gallery-img { transition: transform 0.3s ease; }
                    .gallery-img:hover { transform: scale(1.05); }
                `}
            </style>
        </Card>
    );
}
